import { Capacitor } from '@capacitor/core';
import notificationNavigationService from './notificationNavigationService';

/**
 * Push Notification Service
 *
 * Jedino mesto koje komunicira sa `@capacitor/push-notifications`:
 * - traži/proverava permission,
 * - registruje se za push i prima token,
 * - sluša greške registracije,
 * - prima foreground notifikacije (bez paralelnog state-a - SignalR ostaje
 *   izvor istine),
 * - obrađuje tap na notifikaciju i prosleđuje ga
 *   `notificationNavigationService`-u.
 *
 * Ceo servis je no-op na webu: `Capacitor.isNativePlatform()` je prva i jedina
 * kapija i tada se native plugin nikada ne učitava.
 *
 * Push token se NIKADA ne loguje u celini; za debugging koristi se maskirani
 * oblik.
 */

const isDev = process.env.NODE_ENV !== 'production';

const tokenSubscribers = new Set();
const receivedSubscribers = new Set();
const actionSubscribers = new Set();

let listenersAttached = false;
let listenersPromise = null;
let registrationPromise = null;
let lastRegistrationToken = null;

const isNativePlatform = () => Capacitor.isNativePlatform();

/**
 * Maskiraj push token za logovanje (npr. `abcd…wxyz`).
 * @param {string} token
 * @returns {string}
 */
export const maskPushToken = (token) => {
  if (!token || typeof token !== 'string') return '';
  if (token.length <= 8) return '***';
  return `${token.slice(0, 4)}…${token.slice(-4)}`;
};

const notifySubscribers = (subscribers, payload) => {
  subscribers.forEach((subscriber) => {
    try {
      subscriber(payload);
    } catch (error) {
      console.error('Push notifikacije: obrada događaja nije uspela.', error);
    }
  });
};

let pluginPromise = null;

/**
 * Učitaj Capacitor plugin kao običan objekat (nikada kao thenable).
 *
 * Capacitor plugin je Proxy koji ima i `then` metodu - na native platformi ona
 * odbija poziv sa `"PushNotifications.then()" is not implemented on ios"`.
 * Ako bi se proxy vratio iz async funkcije (`return PushNotifications`) ili
 * await-ovao direktno, JS bi ga tretirao kao Promise-like i pozvao `then()`,
 * što ruši registraciju. Zato se uvek vraća običan objekat sa pluginom, a
 * pozivaoci ga destrukturiraju.
 *
 * @returns {Promise<{PushNotifications: Object}>}
 */
const loadPlugin = () => {
  if (!pluginPromise) {
    pluginPromise = import('@capacitor/push-notifications')
      .then((module) => ({ PushNotifications: module.PushNotifications }))
      .catch((error) => {
        // Ne keširaj neuspelo učitavanje - sledeći pokušaj ide ponovo.
        pluginPromise = null;
        throw error;
      });
  }

  return pluginPromise;
};

const attachListeners = async () => {
  const { PushNotifications } = await loadPlugin();

  await Promise.all([
    PushNotifications.addListener('registration', (token) => {
      if (!token?.value) return;
      lastRegistrationToken = token.value;

      if (isDev) {
        console.info('Push registracija: token primljen.', maskPushToken(token.value));
      }

      notifySubscribers(tokenSubscribers, token.value);
    }),

    PushNotifications.addListener('registrationError', (error) => {
      // Nikada ne logujemo token; backend/provider greške su dovoljne za debug.
      console.warn('Push registracija nije uspela.', error?.error || 'Nepoznata greška');
    }),

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      // Foreground push NE uvodi novi notification state - SignalR već
      // održava listu i toast. Ovde samo prosleđujemo event za buduće hook-ove.
      if (isDev) {
        console.info('Push notifikacija primljena u foreground-u.', {
          id: notification?.id,
          type: notification?.data?.type,
        });
      }
      notifySubscribers(receivedSubscribers, notification);
    }),

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data;

      if (isDev) {
        console.info('Push notifikacija otvorena.', {
          actionId: action?.actionId,
          type: data?.type,
        });
      }

      notifySubscribers(actionSubscribers, action);
      // Centralna navigacija; ako auth/router nisu spremni, servis čuva
      // pending akciju (cold start).
      notificationNavigationService.handleNotificationAction(data);
    }),
  ]);

  listenersAttached = true;
  return true;
};

const pushNotificationService = {
  /**
   * Da li su push notifikacije podržane (native build).
   * @returns {boolean}
   */
  isSupported() {
    return isNativePlatform();
  },

  /**
   * Registruj Capacitor listenere rano (pre React render-a) kako se tap na
   * notifikaciju koji je pokrenuo aplikaciju ne bi izgubio.
   * Idempotentno: listeneri se registruju samo jednom.
   * @returns {Promise<boolean>} True ako su listeneri aktivni
   */
  async initializeListeners() {
    if (!isNativePlatform()) return false;
    if (listenersAttached) return true;
    if (listenersPromise) return listenersPromise;

    listenersPromise = attachListeners()
      .catch((error) => {
        console.warn(
          'Push notifikacije: listeneri nisu registrovani.',
          error?.message || error
        );
        return false;
      })
      .finally(() => {
        listenersPromise = null;
      });

    return listenersPromise;
  },

  /**
   * Trenutni permission status za prijem notifikacija.
   * @returns {Promise<'granted'|'denied'|'prompt'|'prompt-with-rationale'|'unsupported'>}
   */
  async checkPermission() {
    if (!isNativePlatform()) return 'unsupported';
    try {
      const { PushNotifications } = await loadPlugin();
      const status = await PushNotifications.checkPermissions();
      return status?.receive || 'prompt';
    } catch (error) {
      console.warn('Push notifikacije: provera dozvole nije uspela.', error?.message || error);
      return 'unsupported';
    }
  },

  /**
   * Zatraži permission (samo native; na webu se nikada ne poziva).
   * @returns {Promise<'granted'|'denied'|'prompt'|'prompt-with-rationale'|'unsupported'>}
   */
  async requestPermission() {
    if (!isNativePlatform()) return 'unsupported';
    try {
      const { PushNotifications } = await loadPlugin();
      const status = await PushNotifications.requestPermissions();
      return status?.receive || 'denied';
    } catch (error) {
      console.warn('Push notifikacije: zahtev za dozvolu nije uspeo.', error?.message || error);
      return 'unsupported';
    }
  },

  /**
   * Permission flow + registracija kod push provajdera.
   *
   *    granted      -> register()
   *    prompt       -> request -> granted ? register() : denied
   *    denied       -> aplikacija nastavlja normalno (bez registracije)
   *
   * Nikada ne baca grešku - push je dodatna funkcionalnost, ne auth uslov.
   *
   * @returns {Promise<{status: string}>}
   */
  async ensureRegistered() {
    if (!isNativePlatform()) return { status: 'unsupported' };
    if (registrationPromise) return registrationPromise;

    registrationPromise = (async () => {
      try {
        await pushNotificationService.initializeListeners();

        let permission = await pushNotificationService.checkPermission();

        if (permission === 'prompt' || permission === 'prompt-with-rationale') {
          permission = await pushNotificationService.requestPermission();
        }

        if (permission !== 'granted') {
          if (isDev) {
            console.info('Push notifikacije nisu omogućene; aplikacija nastavlja normalno.', {
              permission,
            });
          }
          return { status: permission };
        }

        // Listeneri su već registrovani na početku (pre permission dijaloga),
        // pa je `registration` event sigurno uhvaćen nakon register().
        const { PushNotifications } = await loadPlugin();
        await PushNotifications.register();

        return { status: 'granted' };
      } catch (error) {
        console.warn(
          'Push notifikacije: registracija nije uspela; aplikacija nastavlja normalno.',
          error?.message || error
        );
        return { status: 'error' };
      } finally {
        registrationPromise = null;
      }
    })();

    return registrationPromise;
  },

  /**
   * Poslednji primljeni push token u ovoj sesiji (ako postoji).
   * @returns {string|null}
   */
  getLastRegistrationToken() {
    return lastRegistrationToken;
  },

  /**
   * Pretplata na promene push tokena. Ako je token već primljen (npr. pre
   * nego što se React mountovao), handler se odmah poziva.
   * @param {(token: string) => void} handler
   * @returns {() => void} Unsubscribe
   */
  onRegistrationToken(handler) {
    if (typeof handler !== 'function') return () => {};
    tokenSubscribers.add(handler);

    if (lastRegistrationToken) {
      Promise.resolve().then(() => {
        if (tokenSubscribers.has(handler)) {
          try {
            handler(lastRegistrationToken);
          } catch (error) {
            console.error('Push notifikacije: obrada tokena nije uspela.', error);
          }
        }
      });
    }

    return () => tokenSubscribers.delete(handler);
  },

  /**
   * Pretplata na foreground push događaje (bez dupliranja SignalR state-a).
   * @param {(notification: Object) => void} handler
   * @returns {() => void} Unsubscribe
   */
  onNotificationReceived(handler) {
    if (typeof handler !== 'function') return () => {};
    receivedSubscribers.add(handler);
    return () => receivedSubscribers.delete(handler);
  },

  /**
   * Pretplata na tap na notifikaciju (pre same navigacije).
   * @param {(action: Object) => void} handler
   * @returns {() => void} Unsubscribe
   */
  onNotificationAction(handler) {
    if (typeof handler !== 'function') return () => {};
    actionSubscribers.add(handler);
    return () => actionSubscribers.delete(handler);
  },
};

export default pushNotificationService;

